import { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtSec = (s) => {
  if (s == null) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const shortUA = (ua) => {
  if (!ua) return "—";
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|MSIE)[/ ]([\d.]+)/);
  const browser = m ? `${m[1]} ${m[2].split(".")[0]}` : "Other";
  let os = "Unknown OS";
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return `${browser} · ${os}`;
};

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [summary, setSummary] = useState([]);

  const [period, setPeriod] = useState("daily");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = () => {
    const params = {};
    if (filter !== "all") params.user_id = filter;
    api.get("/agents/sessions", { params }).then((r) => setSessions(r.data));
    api.get("/admin/presence-summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
  };

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [filter]);

  const generate = async () => {
    if (period === "custom" && !customFrom && !customTo) {
      toast.error("Pick a start or end date for the custom range");
      return;
    }
    setGenerating(true);
    try {
      const params = new URLSearchParams({ period });
      if (filter !== "all") params.set("user_id", filter);
      if (period === "custom") {
        if (customFrom) params.set("date_from", new Date(customFrom).toISOString());
        if (customTo) params.set("date_to", new Date(`${customTo}T23:59:59`).toISOString());
      }
      const res = await fetch(`${API}/reports/sessions.xlsx?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      const dl = URL.createObjectURL(blob);
      a.href = dl;
      a.download = `sessions_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success(`Downloaded ${period} report`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setGenerating(false);
    }
  };

  const totalActive = sessions.filter((s) => s.active).length;
  const totalDuration = sessions.reduce((sum, s) => sum + (s.duration_sec || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-sessions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Login Sessions</h1>
          <p className="text-sm text-gray-500 mt-1">Online-time totals, login/logout audit (IP, browser &amp; duration).</p>
        </div>
        <div className="w-56">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="sessions-user-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Online time summary (per-employee aggregates) */}
      <section className="border border-gray-200 rounded-sm overflow-hidden" data-testid="online-time-summary">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/60 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Productivity</p>
            <h2 className="font-display font-bold tracking-tight">Online time by employee</h2>
          </div>
          <p className="text-[11px] text-gray-500">Auto-refreshes every 30s</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-white">
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Employee</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Role</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">State</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Sessions</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Today online</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Total online</th>
                <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Last online</th>
              </tr>
            </thead>
            <tbody data-testid="online-time-summary-rows">
              {summary.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500 text-sm">No online activity yet.</td></tr>
              )}
              {summary.map((s) => (
                <tr key={s.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{s.name}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-gray-500">{s.role}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${s.currently_online ? "text-[#16A34A] border-[#16A34A] bg-green-50" : "text-gray-500 border-gray-300"}`}>
                      {s.currently_online ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{s.sessions_count}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtSec(s.today_online_sec)}</td>
                  <td className="px-4 py-3 font-mono text-xs font-bold">{fmtSec(s.total_online_sec)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{fmtDt(s.last_online_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Report builder */}
      <section className="border border-gray-200 rounded-sm p-4 space-y-3" data-testid="sessions-report-builder">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-wider">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="sessions-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Last 24 hours</SelectItem>
                <SelectItem value="weekly">Last 7 days</SelectItem>
                <SelectItem value="monthly">Last 30 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === "custom" && (
            <>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider">From</Label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="sessions-custom-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] uppercase tracking-wider">To</Label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="sessions-custom-to" />
              </div>
            </>
          )}
          <Button
            onClick={generate}
            disabled={generating}
            className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm h-10"
            data-testid="sessions-generate-button"
          >
            <FileXls size={16} weight="bold" className="mr-1" />
            {generating ? "Generating…" : "Generate report"}
          </Button>
        </div>
        <p className="text-[11px] text-gray-500">
          Pick a preset or define a custom date range, then click <b>Generate report</b> to download as Excel.
        </p>
      </section>

      <div className="grid grid-cols-3 gap-4">
        <div className="border border-gray-200 rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Sessions</p>
          <p className="font-display text-3xl font-black mt-1">{sessions.length}</p>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Active now</p>
          <p className="font-display text-3xl font-black mt-1 text-[#16A34A]">{totalActive}</p>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Total time logged</p>
          <p className="font-display text-3xl font-black mt-1">{fmtSec(totalDuration)}</p>
        </div>
      </div>

      <div className="border border-gray-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/60">
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Employee</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Login at</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Logout at</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Duration</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">IP</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Browser · OS</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">State</th>
            </tr>
          </thead>
          <tbody data-testid="sessions-list">
            {sessions.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500 text-sm">No sessions yet.</td></tr>}
            {sessions.map((s) => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{s.user_name}</td>
                <td className="px-4 py-3 text-gray-700">{fmtDt(s.login_at)}</td>
                <td className="px-4 py-3 text-gray-700">{fmtDt(s.logout_at)}</td>
                <td className="px-4 py-3 font-mono text-xs">{fmtSec(s.duration_sec)}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.ip_address || "—"}</td>
                <td className="px-4 py-3 text-xs text-gray-600" title={s.user_agent}>{shortUA(s.user_agent)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-sm ${s.active ? "text-[#16A34A] border-[#16A34A] bg-green-50" : "text-gray-500 border-gray-300"}`}>
                    {s.active ? "Active" : "Closed"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
